import React from 'react';
import { Link } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const { FiArrowLeft, FiTrendingUp, FiCalendar } = FiIcons;

const smicHistory = [
  { date: 'Jan 2014', value: 9.53, fullDate: '1er janvier 2014', increase: '+1,1%' },
  { date: 'Jan 2015', value: 9.61, fullDate: '1er janvier 2015', increase: '+0,8%' },
  { date: 'Jan 2016', value: 9.67, fullDate: '1er janvier 2016', increase: '+0,6%' },
  { date: 'Jan 2017', value: 9.76, fullDate: '1er janvier 2017', increase: '+0,9%' },
  { date: 'Jan 2018', value: 9.88, fullDate: '1er janvier 2018', increase: '+1,2%' },
  { date: 'Jan 2019', value: 10.03, fullDate: '1er janvier 2019', increase: '+1,5%' },
  { date: 'Jan 2020', value: 10.15, fullDate: '1er janvier 2020', increase: '+1,2%' },
  { date: 'Jan 2021', value: 10.25, fullDate: '1er janvier 2021', increase: '+0,99%' },
  { date: 'Oct 2021', value: 10.48, fullDate: '1er octobre 2021', increase: '+2,2%' },
  { date: 'Jan 2022', value: 10.57, fullDate: '1er janvier 2022', increase: '+0,9%' },
  { date: 'Mai 2022', value: 10.85, fullDate: '1er mai 2022', increase: '+2,65%' },
  { date: 'Août 2022', value: 11.07, fullDate: '1er août 2022', increase: '+2,01%' },
  { date: 'Jan 2023', value: 11.27, fullDate: '1er janvier 2023', increase: '+1,81%' },
  { date: 'Mai 2023', value: 11.52, fullDate: '1er mai 2023', increase: '+2,22%' },
  { date: 'Jan 2024', value: 11.65, fullDate: '1er janvier 2024', increase: '+1,13%' },
  { date: 'Nov 2024', value: 11.88, fullDate: '1er novembre 2024', increase: '+2,0%' },
  { date: 'Jan 2026', value: 12.02, fullDate: '1er janvier 2026', increase: '+1,17%' },
];

export default function EvolutionSmic() {
  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: '#1e293b' },
      padding: [10, 15],
      formatter: function (params) {
        const data = params[0].data;
        return `
          <div style="font-size: 13px; color: #64748b; margin-bottom: 4px;">${data.fullDate}</div>
          <div style="font-size: 16px; font-weight: bold; color: #4f46e5;">${data.value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € brut / h</div>
        `;
      }
    },
    grid: {
      left: '2%',
      right: '4%',
      bottom: '3%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: smicHistory.map(item => item.date),
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisLabel: { 
        color: '#64748b',
        fontSize: 11,
        margin: 12
      },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      min: 9.0,
      max: 12.5,
      interval: 0.5,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { 
        lineStyle: { 
          color: '#f1f5f9', 
          type: 'dashed' 
        } 
      },
      axisLabel: {
        color: '#64748b',
        fontSize: 12,
        formatter: '{value} €'
      }
    },
    series: [
      {
        name: 'SMIC Horaire Brut',
        type: 'line',
        smooth: 0.3,
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: { 
          color: '#4f46e5',
          borderColor: '#ffffff',
          borderWidth: 2
        },
        lineStyle: {
          width: 3,
          color: '#4f46e5'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(79, 70, 229, 0.2)' },
              { offset: 1, color: 'rgba(79, 70, 229, 0.0)' }
            ]
          }
        },
        data: smicHistory.map(item => ({
          value: item.value,
          fullDate: item.fullDate
        }))
      }
    ]
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 transition-colors mb-8">
        <SafeIcon icon={FiArrowLeft} />
        Retour au calculateur
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <SafeIcon icon={FiTrendingUp} className="text-indigo-600 text-lg" />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900">Évolution du SMIC</h1>
          <p className="text-slate-500 text-sm mt-1">Historique des revalorisations du taux horaire brut en France</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 ring-1 ring-slate-900/5 shadow-sm mb-8">
        <div className="h-[400px] w-full">
          <ReactECharts 
            option={chartOptions} 
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden ring-1 ring-slate-900/5 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">Date d'application</th>
                <th className="px-6 py-4">Taux Horaire Brut</th>
                <th className="px-6 py-4">Revalorisation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...smicHistory].reverse().map((item, index) => (
                <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4 flex items-center gap-2 text-slate-700">
                    <SafeIcon icon={FiCalendar} className="text-slate-400" />
                    {item.fullDate}
                  </td>
                  <td className="px-6 py-4 font-medium text-slate-900">
                    <span className="text-emerald-600">€</span> {item.value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700">
                      {item.increase}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-6 text-center">
        Les données historiques sont basées sur les décrets officiels publiés au Journal Officiel de la République Française.
      </p>
    </div>
  );
}